import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import useTheme from "@/utils/useTheme";
import Navbar from "@/components/sections/Navbar";
import Hero from "@/components/sections/Hero";
import QuizBanner from "@/components/sections/QuizBanner";
import PlatformIntro from "@/components/sections/PlatformIntro";
import HealthDimensions from "@/components/sections/HealthDimensions";
import WhyMonitoring from "@/components/sections/WhyMonitoring";
import Tools from "@/components/sections/Tools";
import HowItWorks from "@/components/sections/HowItWorks";
import ScreeningDrives from "@/components/sections/ScreeningDrives";
import Contact from "@/components/sections/Contact";
import Footer from "@/components/sections/Footer";
import BoneAHPage from "@/pages/BoneAHPage";
import BoneAHAboutPage from "@/pages/BoneAHAboutPage";
import BoneAHChooserPage from "@/pages/BoneAHChooserPage";
import BoneAgePage from "@/pages/BoneAgePage";
import BoneAgeAboutPage from "@/pages/BoneAgeAboutPage";
import GrowthAssessmentPage from "@/pages/GrowthAssessmentPage";
import GrowthAssessmentAboutPage from "@/pages/GrowthAssessmentAboutPage";
import NINNutriGuidePage from "@/pages/NINNutriGuidePage";
import NINNutriGuideAboutPage from "@/pages/NINNutriGuideAboutPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import CompleteProfilePage from "@/pages/CompleteProfilePage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import TermsPage from "@/pages/TermsPage";
import RefundPolicyPage from "@/pages/RefundPolicyPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import ShippingPolicyPage from "@/pages/ShippingPolicyPage";
import YoungAthletesPage from "@/pages/YoungAthletesPage";
import HeightEnhancementPage from "@/pages/HeightEnhancementPage";
import DevelopersPage from "@/pages/DevelopersPage";
import ParentQuizPage from "@/pages/ParentQuizPage";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import CreditsGuard from "@/components/CreditsGuard";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import { AuthProvider } from "@/context/AuthContext";

const Home = () => {
  const { theme } = useTheme();

  useEffect(() => {
    // Honour scroll target set by other pages (e.g. Young Athletes' "Book a Screening")
    const target = sessionStorage.getItem("scrollToOnLoad") || (window.location.hash === "#contact" ? "contact" : null);
    if (target) {
      sessionStorage.removeItem("scrollToOnLoad");
      setTimeout(() => {
        document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
      }, 250);
    }
  }, []);

  return (
    <div className={`min-h-screen ${theme}`}>
      <Navbar theme={theme} />
      <Hero theme={theme} />
      <QuizBanner />
      <WhyMonitoring theme={theme} />
      <PlatformIntro theme={theme} />
      <HealthDimensions theme={theme} />
      <Tools theme={theme} />
      <HowItWorks theme={theme} />
      <ScreeningDrives theme={theme} />
      <Contact theme={theme} />
      <Footer theme={theme} />
      <Toaster position="top-right" />
    </div>
  );
};

/**
 * Detects Emergent OAuth callback (session_id in URL fragment) BEFORE any ProtectedRoute
 * or /me check runs. This prevents race conditions where the auth cookie hasn't been
 * set yet but ProtectedRoute would redirect to /login.
 */
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/tools/boneah/about" element={<BoneAHAboutPage />} />
      <Route path="/tools/bone-age/about" element={<BoneAgeAboutPage />} />
      <Route path="/tools/growth/about" element={<GrowthAssessmentAboutPage />} />
      <Route path="/tools/nin-nutriguide/about" element={<NINNutriGuideAboutPage />} />
      <Route
        path="/tools/boneah"
        element={
          <ProtectedRoute>
            <BoneAHChooserPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/bone-age"
        element={
          <ProtectedRoute>
            <CreditsGuard>
              <BoneAgePage />
            </CreditsGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/growth"
        element={
          <ProtectedRoute>
            <CreditsGuard tool="growth">
              <GrowthAssessmentPage />
            </CreditsGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/boneah-legacy"
        element={
          <ProtectedRoute>
            <CreditsGuard>
              <BoneAHPage />
            </CreditsGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/nin-nutriguide"
        element={
          <ProtectedRoute>
            <CreditsGuard tool="nutrition">
              <NINNutriGuidePage />
            </CreditsGuard>
          </ProtectedRoute>
        }
      />

      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
      <Route path="/young-athletes" element={<YoungAthletesPage />} />
      <Route path="/tools/height-enhancement" element={<HeightEnhancementPage />} />
      <Route path="/developers" element={<DevelopersPage />} />
      <Route path="/parent-quiz" element={<ProtectedRoute><ParentQuizPage /></ProtectedRoute>} />
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute allowNoRole>
            <CompleteProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout/success"
        element={
          <ProtectedRoute>
            <CheckoutSuccessPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AnalyticsTracker />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
